import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtGHS, fmtDate, fmtDateTime } from "@/lib/format";
import { Undo2, PackageX } from "lucide-react";
import { toast } from "sonner";

const REASONS = [["expired", "Expired"], ["damaged", "Damaged"], ["recall", "Recall"], ["overstock", "Overstock"]];

export default function Returns() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [batches, setBatches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ batch_id: "", supplier_id: "", quantity: 1, reason: "expired", credit_amount: 0, note: "" });

  const load = () => api.get("/returns").then((r) => setRows(r.data)).catch(() => {});
  const loadBatches = () => api.get("/batches").then((r) => setBatches(r.data.filter((b) => b.quantity_on_hand > 0))).catch(() => {});
  useEffect(() => {
    load(); loadBatches();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const selBatch = batches.find((b) => b.id === form.batch_id);
  const pickBatch = (id) => {
    const b = batches.find((x) => x.id === id);
    setForm((f) => ({ ...f, batch_id: id, supplier_id: b?.supplier_id || f.supplier_id || "",
      quantity: 1, credit_amount: Number((1 * (b?.cost_price || 0)).toFixed(2)) }));
  };
  const setQty = (v) => {
    const q = Number(v) || 0;
    setForm((f) => ({ ...f, quantity: v, credit_amount: Number((q * (selBatch?.cost_price || 0)).toFixed(2)) }));
  };
  const openNew = () => { setForm({ batch_id: "", supplier_id: "", quantity: 1, reason: "expired", credit_amount: 0, note: "" }); setOpen(true); };
  const save = async () => {
    if (!form.batch_id) return toast.error("Select a batch to return");
    if (!form.supplier_id) return toast.error("Select the supplier to return to");
    try {
      await api.post("/returns", { batch_id: form.batch_id, supplier_id: form.supplier_id,
        quantity: Number(form.quantity), reason: form.reason, credit_amount: Number(form.credit_amount), note: form.note });
      toast.success("Return recorded & stock reduced"); setOpen(false); load(); loadBatches();
    } catch (e) { toast.error(apiError(e)); }
  };
  const credit = async (row) => {
    try { await api.post(`/returns/${row.id}/credit`); toast.success("Credit note recorded"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Returns to Supplier" subtitle="Send expired or damaged stock back and track supplier credit notes"
        actions={can("procurement.returns") && <Button data-testid="new-return-btn" onClick={openNew}><Undo2 className="h-4 w-4 mr-1.5" /> New Return</Button>} />
      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Return No.</th><th className="py-2.5 px-3">Medicine</th><th className="py-2.5 px-3">Batch</th>
            <th className="py-2.5 px-3">Supplier</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3">Reason</th>
            <th className="py-2.5 px-3 text-right">Qty</th><th className="py-2.5 px-3 text-right">Credit</th><th className="py-2.5 px-3">Status</th>
            <th className="py-2.5 px-3">When</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={11} className="py-10 text-center text-muted-foreground"><PackageX className="h-8 w-8 mx-auto mb-2 opacity-40" />No returns yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/50">
                <td className="py-2 px-3 font-mono text-xs">{r.return_no}</td>
                <td className="py-2 px-3"><div className="font-medium text-foreground">{r.generic_name}</div><div className="text-xs text-muted-foreground">{r.sku}</div></td>
                <td className="py-2 px-3 font-mono text-xs">{r.batch_no}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.supplier_name}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.branch_name}</td>
                <td className="py-2 px-3 capitalize">{r.reason}</td>
                <td className="py-2 px-3 text-right font-mono">{r.quantity}</td>
                <td className="py-2 px-3 text-right font-mono text-xs">{fmtGHS(r.credit_amount)}</td>
                <td className="py-2 px-3"><StatusBadge value={r.status} label={r.status} /></td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(r.created_at)}</td>
                <td className="py-2 px-3 text-right">{can("procurement.returns") && r.status === "pending" && <Button size="sm" variant="outline" data-testid={`credit-return-${r.return_no}`} onClick={() => credit(r)}>Record credit</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Return to Supplier</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Batch</Label>
              <Select value={form.batch_id} onValueChange={pickBatch}>
                <SelectTrigger data-testid="return-batch-select"><SelectValue placeholder="Select a batch in stock" /></SelectTrigger>
                <SelectContent>{batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.generic_name} · {b.batch_no} · {b.branch_name} · {b.quantity_on_hand} on hand</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selBatch && <div className="text-xs text-muted-foreground">Expiry {fmtDate(selBatch.expiry_date)} · unit cost {fmtGHS(selBatch.cost_price)} · {selBatch.quantity_on_hand} available</div>}
            <div><Label>Supplier</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
                <SelectTrigger data-testid="return-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Quantity</Label><Input type="number" data-testid="return-qty-input" value={form.quantity} onChange={(e) => setQty(e.target.value)} /></div>
              <div><Label>Reason</Label>
                <Select value={form.reason} onValueChange={(v) => setForm((f) => ({ ...f, reason: v }))}>
                  <SelectTrigger data-testid="return-reason-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Credit (GHS)</Label><Input type="number" step="0.01" data-testid="return-credit-input" value={form.credit_amount} onChange={(e) => setForm((f) => ({ ...f, credit_amount: e.target.value }))} /></div>
            </div>
            <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="submit-return-btn" onClick={save}>Record return</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
